import {
  UnifiedLiquidityEnum,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidityAvailableSymbols,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import type { PreTradeResponseData } from '../redux/preTradeApiTypes';

function getPreTradeOrderSymbol(scenario: PreTradeResponseData | undefined) {
  return scenario?.NewOrderSingle?.Symbol ?? scenario?.Symbol;
}

function getPreTradeMarketSelections(scenario: PreTradeResponseData | undefined) {
  const nosMarkets = scenario?.NewOrderSingle?.Markets ?? [];
  const scenarioMarkets = scenario?.Markets ?? [];

  return {
    markets: uniq(compact([...scenarioMarkets.map(m => m.Market), ...nosMarkets.map(m => m.Market)])),
    marketAccounts: uniq(compact(nosMarkets.map(m => m.MarketAccount))),
  };
}

/**
 * Derives unified-liquidity symbol display props for pre-trade (same inputs as market card header).
 */
export function usePreTradeUnifiedLiquiditySymbolDisplay(scenario: PreTradeResponseData | undefined) {
  const symbol = getPreTradeOrderSymbol(scenario);
  const isUnifiedLiquidity = scenario?.NewOrderSingle?.Parameters?.UnifiedLiquidity === UnifiedLiquidityEnum.Enabled;

  const security = useSecurity(symbol);
  const { securitiesBySymbol } = useSecuritiesContext();
  const { markets, marketAccounts } = useMemo(() => getPreTradeMarketSelections(scenario), [scenario]);

  const availableSymbols = useUnifiedLiquidityAvailableSymbols({
    markets,
    marketAccounts,
    symbol,
  });

  const quoteCurrencies = useMemo(() => {
    if (!isUnifiedLiquidity || !security) {
      return [];
    }

    const quoteCurrenciesSet = new Set<string>();
    for (const availableSymbol of availableSymbols) {
      const availableSecurity = securitiesBySymbol.get(availableSymbol);
      if (availableSecurity) {
        quoteCurrenciesSet.add(availableSecurity.QuoteCurrency);
      }
    }

    return Array.from(quoteCurrenciesSet);
  }, [isUnifiedLiquidity, security, availableSymbols, securitiesBySymbol]);

  return {
    isUnifiedLiquidity,
    security,
    quoteCurrencies,
    availableSymbols,
    unifiedLiquidity: isUnifiedLiquidity ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
  };
}
